import React from 'react';
import type { RealYieldCommentary } from '../types';

interface Props {
    commentary?: RealYieldCommentary | null;
}

export const RealYieldCommentaryCard: React.FC<Props> = ({ commentary }) => {
    if (!commentary) return null;

    const isBearish = commentary.equities.outlook.includes('🔴');
    const isBullish = commentary.equities.outlook.includes('🟢');

    let headerClass = 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100';

    if (isBearish) {
        headerClass = 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200';
    } else if (isBullish) {
        headerClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
    }

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-full">
            {/* Header */}
            <div className={`px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center ${headerClass}`}>
                <div>
                    <h2 className="text-xs font-bold uppercase tracking-wider opacity-80">Macro Regime</h2>
                    <h3 className="text-lg font-black">{commentary.regime_name}</h3>
                </div>
                <div className="text-right">
                    <h2 className="text-xs font-bold uppercase tracking-wider opacity-80">Equity Outlook</h2>
                    <h3 className="text-md font-bold">{commentary.equities.outlook}</h3>
                </div>
            </div>

            {/* Content */}
            <div className="p-5 flex-1 flex flex-col gap-6 overflow-y-auto">
                <div className="space-y-2">
                    {commentary.description.map((desc, idx) => (
                        <p key={idx} className="text-sm font-medium text-slate-700 dark:text-slate-300 flex gap-2">
                            <span className="text-indigo-500">•</span>
                            <span>{desc}</span>
                        </p>
                    ))}
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-100 dark:border-slate-700/50">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Macro Dynamics</h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">Inflation Anchors:</span>
                            <span className="text-slate-600 dark:text-slate-400">{commentary.macro_dynamics.inflation_expectations}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">Cost of Capital:</span>
                            <span className="text-slate-600 dark:text-slate-400">{commentary.macro_dynamics.cost_of_capital}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">Fed Posture:</span>
                            <span className="text-slate-600 dark:text-slate-400">{commentary.macro_dynamics.fed_posture}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 dark:bg-emerald-900/10 p-3 rounded border border-emerald-100 dark:border-emerald-800/30">
                        <h5 className="text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-2 uppercase tracking-wide">Favor</h5>
                        <ul className="space-y-1 text-sm text-emerald-900 dark:text-emerald-200">
                            {commentary.recommendation.favor.map((item, idx) => (
                                <li key={idx} className="flex gap-1"><span className="text-emerald-500">✓</span> {item}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-rose-50 dark:bg-rose-900/10 p-3 rounded border border-rose-100 dark:border-rose-800/30">
                        <h5 className="text-xs font-bold text-rose-700 dark:text-rose-400 mb-2 uppercase tracking-wide">Avoid</h5>
                        <ul className="space-y-1 text-sm text-rose-900 dark:text-rose-200">
                            {commentary.recommendation.avoid.map((item, idx) => (
                                <li key={idx} className="flex gap-1"><span className="text-rose-500">✕</span> {item}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
